import { Location } from '@/entities/League';
import { Photo } from '@/entities/shared/user/Photo';
import { SystemLogo } from '@/entities/SystemLogo';

export enum AvailabilityStatus {
	UNDEFINED = 'TEAM_LEAGUE_PLAYER_AVAILABILITY_STATUS_UNDEFINED',
	AVAILABLE = 'TEAM_LEAGUE_PLAYER_AVAILABILITY_STATUS_YES',
	UNAVAILABLE = 'TEAM_LEAGUE_PLAYER_AVAILABILITY_STATUS_NO',
	MAYBE = 'TEAM_LEAGUE_PLAYER_AVAILABILITY_STATUS_MAYBE'
}

export const AVAILABILITY_STATUS_LABELS: Record<AvailabilityStatus, string> = {
	[AvailabilityStatus.AVAILABLE]: 'Yes',
	[AvailabilityStatus.MAYBE]: 'Maybe',
	[AvailabilityStatus.UNAVAILABLE]: 'No',
	[AvailabilityStatus.UNDEFINED]: 'No response'
};

export const AVAILABILITY_STATUS_VALUE: Record<AvailabilityStatus, number> = {
	[AvailabilityStatus.UNDEFINED]: 0,
	[AvailabilityStatus.AVAILABLE]: 1,
	[AvailabilityStatus.UNAVAILABLE]: 2,
	[AvailabilityStatus.MAYBE]: 3
};

/** Statuses a manager can assign in the edit modal (UNDEFINED is read-only / "no response"). Order matches the design: Yes, No, Maybe. */
export const SELECTABLE_AVAILABILITY_STATUSES = [AvailabilityStatus.AVAILABLE, AvailabilityStatus.UNAVAILABLE, AvailabilityStatus.MAYBE] as const;

export interface AvailabilityLocation extends Location {
	uuid: string;
	venue: string;
	altName: string;
	streetAddress: string;
	zip: string;
}

export interface AvailabilityPlayer {
	playerUuid: string;
	firstName: string;
	lastName: string;
	status: AvailabilityStatus;
	comment: string | null;
	dateUpdated: string | null;
	updatedByUuid: string | null;
	phone: string | null;
	internationalCallingCode: string | null;
	sendTextAlerts: boolean;
	playerImage: Photo | null;
}

export interface AvailabilityGameDay {
	matchDayDate: string;
	locationUuid: string | null;
	location: AvailabilityLocation | null;
	players: AvailabilityPlayer[];
	image?: string | null;
	// Set when this game day was flattened from a matchup group (Scheduled Event version). When present,
	// the edit request is sent as a group update (matchup_group_uuid + group_comment) and the card shows
	// the group's logo + title in its header.
	matchupGroupUuid?: string | null;
	groupTitle?: string | null;
	groupComment?: string | null;
}

export interface AvailabilityMatchupGroup {
	matchupGroupUuid: string;
	// Scheduled-event logo (only present for matchup groups, never for regular game days).
	matchupGroupLogo?: SystemLogo | null;
	title: string;
	startDate: string;
	endDate: string;
	comment: string | null;
	locationUuid: string | null;
	location: AvailabilityLocation | null;
	gameDays: AvailabilityGameDay[];
}

export interface PlayerAvailabilityResults {
	teamLeagueUuid: string;
	currentSeasonUuid: string;
	seasonTitle: string;
	divisionTitle: string;
	districtTitle: string;
	teamTitle: string;
	/** Scheduled Event version (useScheduleGroups). Each group carries its own nested gameDays. */
	matchupGroups: AvailabilityMatchupGroup[];
	/** Gameday version. Empty when the league uses schedule groups. */
	gameDays: AvailabilityGameDay[];
}
